import {
  Cryoprotectantv4,
  ICrystalDTO,
  IFOVSizeDTO,
  ILightPathDTO,
  IROIDTO,
  IRectangleF,
  IRegionAnnotationDTO,
  IRegionPointDTO,
  IRegionRulerDTO,
  IScribblesDataDTO,
  Soakingv4,
} from 'business/models/Models';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import { DrawingsData } from 'redux/reducers/wellReducer';

export interface AnnotationDrawing extends IRegionAnnotationDTO {
  dbX: number;
  dbY: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface POIDrawing extends IRegionPointDTO {
  locked: boolean;
  dbX: number;
  dbY: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface CrystalDrawing extends ICrystalDTO {
  color: string;
  locked: boolean;
  isChecked: boolean;
  dbX: number;
  dbY: number;
  dbWidth: number;
  dbHeight: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface ROIDrawing
  extends Omit<IROIDTO, 'xCenterOffSet' | 'yCenterOffSet' | 'xWidth' | 'yHeight'> {
  locked: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  dropNumber?: number;
  regionId?: number;
  dbWidth: number;
  dbHeight: number;
  dbXCenterOffset: number;
  dbYCenterOffset: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface RulerDrawing extends IRegionRulerDTO {
  dbX1: number;
  dbY1: number;
  dbX2: number;
  dbY2: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface ScribbleDrawing
  extends Omit<
    IScribblesDataDTO,
    'scribbleStartPointsXYofWell' | 'xWidthyHeightCaptureResult'
  > {
  id: number;
  points?: number[];
  x: number;
  y: number;
  color: string;
  dbX: number;
  dbY: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export interface LightPathDrawing
  extends Omit<
    ILightPathDTO,
    'id' | 'rectangleF' | 'minFOVSize' | 'maxFOVSize' | 'validSizeList'
  > {
  id: number;
  rectangleF: IRectangleF;
  minFOVSize: IFOVSizeDTO;
  maxFOVSize: IFOVSizeDTO;
  validSizeList: IFOVSizeDTO[];
  zIndex: number;
}

export interface DropLocationDrawing {
  id: number;
  locked: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  regionId?: number;
  dbWidth: number;
  dbHeight: number;
  dbXCenterOffset: number;
  dbYCenterOffset: number;
  zIndex: number;
  isNewDrawing?: boolean;
}

export type DrawingsType =
  | AnnotationDrawing
  | POIDrawing
  | CrystalDrawing
  | ROIDrawing
  | RulerDrawing
  | ScribbleDrawing
  | LightPathDrawing
  | DropLocationDrawing;

export type SelectedDrawingDetails = {
  drawingId: number;
  drawingType: DrawingType;
  plateId: number;
  wellNumber: number;
  dropNumber: number;
  regionName: string;
  drawing?: DrawingsType;
};

export type RulerUpdateDetails = {
  id?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  dbX1?: number;
  dbY1?: number;
  dbX2?: number;
  dbY2?: number;
  color?: string;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type ScribbleUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  points?: number[];
  color?: string;
  dbX?: number;
  dbY?: number;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type PoiUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  color?: string;
  name?: string;
  dbX?: number;
  dbY?: number;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type CrystalUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  color?: string;
  isChecked?: boolean;
  locked?: boolean;
  dbX?: number;
  dbY?: number;
  dbWidth?: number;
  dbHeight?: number;
  cryoprotectants?: Cryoprotectantv4[];
  soakings?: Soakingv4[];
  notes?: string;
  dateFrozen?: Date;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type RoiUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  regionName?: string;
  color?: string;
  locked?: boolean;
  imagingEnabled?: boolean;
  dbWidth?: number;
  dbHeight?: number;
  dbXCenterOffset?: number;
  dbYCenterOffset?: number;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type AnnotationUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  text?: string;
  bgColor?: string;
  fontFamily?: string;
  size?: number;
  textDecoration?: string;
  fontStyle?: string;
  dbX?: number;
  dbY?: number;
  expanded?: boolean;
  zIndex?: number;
  isNewDrawing?: boolean;
};

export type DropLocationUpdateDetails = {
  id?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  dbX?: number;
  dbY?: number;
  dbWidth?: number;
  dbHeight?: number;
  dbXCenterOffset?: number;
  dbYCenterOffset?: number;
  parentRegionId?: number;
  regionName?: string;
  zIndex?: number;
  isNewDrawing?: boolean;
};
export type DrawingUpdateDetails =
  | RulerUpdateDetails
  | ScribbleUpdateDetails
  | PoiUpdateDetails
  | CrystalUpdateDetails
  | RoiUpdateDetails
  | AnnotationUpdateDetails
  | DropLocationUpdateDetails;

export type DrawingFetchRequestParams = {
  plateId: number;
  wellNumber: number;
  batchId: number;
  dropNumber: number;
  captureProfileId: number;
  regionName: string;
  tpObject: TransformPoints;
};

export enum DrawingType {
  Scribble,
  Ruler,
  ROI,
  POI,
  Annotation,
  CRYSTAL,
  LightPath,
  DropLocation,
}

export const DrawingKey: { [key in DrawingType]: keyof DrawingsData } = {
  [DrawingType.CRYSTAL]: 'crystals',
  [DrawingType.Annotation]: 'annotations',
  [DrawingType.POI]: 'pois',
  [DrawingType.ROI]: 'rois',
  [DrawingType.Ruler]: 'rulers',
  [DrawingType.Scribble]: 'scribbles',
  [DrawingType.LightPath]: 'lightPaths',
  [DrawingType.DropLocation]: 'dropLocations',
};

export type DrawingUpdateRequestParams = {
  plateId: number;
  wellNumber: number;
  dropNumber: number;
  regionName: string;
  drawingId: number;
  drawingType: DrawingType;
  drawingUpdateDetails: DrawingUpdateDetails;
  tpObject?: TransformPoints;
};

export const resizableShapes: DrawingType[] = [
  DrawingType.CRYSTAL,
  DrawingType.Annotation,
  DrawingType.ROI,
  DrawingType.DropLocation,
];

export type DropImageRequestParams = {
  pid: number;
  wellNumber: number;
  batchId: number;
  dropNumber: number;
  cpID: number;
  regionID: number;
  bestFocusImageRequired?: boolean;
  thumbnailImageType?: string;
};

export type NewDrawingRequestParams = {
  plateId: number;
  wellNumber: number;
  dropNumber: number;
  cpId: number;
  imageBatchId: number;
  regionName: string;
  drawingType: DrawingType;
  drawingUpdateDetails?: DrawingUpdateDetails;
  tpObject: TransformPoints;
};

export enum OverlayTools {
  POINTER,
  PAN,
  ROI,
  POI,
  SCRIBBLE,
  ANNOTATION,
  RULER,
  CRYSTAL,
  DROPLOCATION,
}

export const DrawingTypeToOverlayToolMapping = {
  [DrawingType.Scribble]: OverlayTools.SCRIBBLE,
  [DrawingType.Ruler]: OverlayTools.RULER,
  [DrawingType.ROI]: OverlayTools.ROI,
  [DrawingType.POI]: OverlayTools.POI,
  [DrawingType.Annotation]: OverlayTools.ANNOTATION,
  [DrawingType.CRYSTAL]: OverlayTools.CRYSTAL,
  [DrawingType.DropLocation]: OverlayTools.DROPLOCATION,
};

export const minDrawingWidth = 30;
export const minDrawingHeight = 30;
export const poiPointSize = 15;
export const rulerEndPointsRadius = 3;
export const lockDrawingType: DrawingType[] = [DrawingType.ROI, DrawingType.POI];
export const colorDrawingType: DrawingType[] = [DrawingType.Ruler, DrawingType.Scribble];
export const colorOverlayTools = [OverlayTools.RULER, OverlayTools.SCRIBBLE];
export const NEW_CRYSTAL_COLOUR = '#39FF14';
